import { AnimatePresence } from 'framer-motion';
import React, { useRef, useState } from 'react';

import SpaceX from '../../../../components/SpaceX';
import SpaceY from '../../../../components/SpaceY';
import { Diary } from '../../../../type/recipe';
import { MAX_WIDTH_639PX } from '../../../../utils/common';
import { Z_INDEX } from '../../../../utils/common/zIndex';
import { usePreventBodyScroll } from '../../../../utils/hook/hook';
import useClientMediaQuery from '../../../../utils/hook/useClientMediaQuery';
import { useRouterQuery } from '../../../../utils/hook/useRouterQuery';

import DiaryShorts from './DiaryShorts';

type ViewMode = 'SHOW' | 'HIDE';
export type DiaryShortsModalQuery = { shortsDiaryId?: number };

const DiaryShortsModal = ({
  diaries,
  loadNextPage,
  onSuccessToggleLike,
}: {
  diaries: Diary[];
  loadNextPage: () => void;
  onSuccessToggleLike: (diaryId: number) => void;
}) => {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('SHOW');

  const { query } = useRouterQuery<DiaryShortsModalQuery>();
  const isMobile = useClientMediaQuery({ query: MAX_WIDTH_639PX });

  const selectedDiary = diaries.find(({ id }) => id === query.shortsDiaryId);
  const open = isMobile && !!selectedDiary;

  const toggleViewMode = () =>
    setViewMode((prev) => (prev === 'SHOW' ? 'HIDE' : 'SHOW'));

  usePreventBodyScroll(open);

  return (
    <AnimatePresence>
      {selectedDiary && (
        <DiaryShorts.Root diary={selectedDiary}>
          <DiaryShorts.CommentBottomSheet />
          {open && (
            <>
              <DiaryShorts.AnimatePresenceDiv
                ref={modalRef}
                className={`fixed inset-0 h-full w-full snap-y snap-mandatory overflow-y-scroll bg-black scrollbar-hide`}
                style={{ zIndex: Z_INDEX.DIARY_SHORTS.MODAL }}
              >
                <AnimatePresence>
                  {viewMode === 'SHOW' && (
                    <DiaryShorts.AnimatePresenceDiv>
                      <DiaryShorts.Header />
                    </DiaryShorts.AnimatePresenceDiv>
                  )}
                </AnimatePresence>

                <DiaryShorts.VerticalPhotoWrapper
                  diaries={diaries}
                  toggleViewMode={toggleViewMode}
                  modalRef={modalRef}
                  loadNextPage={loadNextPage}
                  render={(diary) => (
                    <DiaryShorts.HorizontalPhotoWrapper
                      diary={diary}
                      render={(photo, index) => (
                        <DiaryShorts.Photo
                          diary={diary}
                          photo={photo}
                          index={index}
                        />
                      )}
                    />
                  )}
                ></DiaryShorts.VerticalPhotoWrapper>
                <AnimatePresence>
                  {viewMode === 'HIDE' && (
                    <DiaryShorts.AnimatePresenceDiv>
                      <DiaryShorts.PhotoIndicator />
                    </DiaryShorts.AnimatePresenceDiv>
                  )}
                </AnimatePresence>

                <DiaryShorts.Helper />

                <DiaryShorts.Footer>
                  <AnimatePresence>
                    {viewMode === 'SHOW' && (
                      <DiaryShorts.AnimatePresenceDiv>
                        <DiaryShorts.Ingredients />

                        <SpaceY height={12} />
                        <DiaryShorts.Content />
                      </DiaryShorts.AnimatePresenceDiv>
                    )}
                  </AnimatePresence>

                  <SpaceY height={12} />
                  <div className="flex-between h-48px">
                    <DiaryShorts.ReferencedRecipe />

                    <SpaceX width={8} />
                    <DiaryShorts.Comment />
                    <DiaryShorts.Like
                      onSuccessToggleLike={onSuccessToggleLike}
                    />
                  </div>
                </DiaryShorts.Footer>
              </DiaryShorts.AnimatePresenceDiv>
            </>
          )}
        </DiaryShorts.Root>
      )}
    </AnimatePresence>
  );
};

export default DiaryShortsModal;
